import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Pencil, Trash2, KeyRound, Copy, Check } from 'lucide-react';
import { format } from 'date-fns';

const roleLabels = { manager: 'Manager', cleaner: 'Housekeeping', maintenance: 'Maintenance', admin: 'Admin' };
const roleColors = { admin: 'bg-purple-50 text-purple-700 border-purple-200', manager: 'bg-blue-50 text-blue-700 border-blue-200', cleaner: 'bg-emerald-50 text-emerald-700 border-emerald-200', maintenance: 'bg-amber-50 text-amber-700 border-amber-200' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  // User dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const defaultUserForm = { email: '', first_name: '', last_name: '', password: '', phone: '', role: 'manager' };
  const [userForm, setUserForm] = useState(defaultUserForm);

  // Code dialog
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [codeRole, setCodeRole] = useState('manager');
  const [codeLabel, setCodeLabel] = useState('');

  const fetchData = async () => {
    try {
      const [u, c] = await Promise.all([api.get('/users'), api.get('/staff-codes')]);
      setUsers(u.data);
      setCodes(c.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  // User CRUD
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { password, email, ...updateData } = userForm;
        await api.put(`/users/${editing.id}`, updateData);
      } else {
        await api.post('/users', userForm);
      }
      setUserDialogOpen(false); setEditing(null); setUserForm(defaultUserForm); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (u) => {
    setEditing(u);
    setUserForm({ email: u.email, first_name: u.first_name, last_name: u.last_name, password: '', phone: u.phone || '', role: u.role });
    setUserDialogOpen(true);
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete user?')) return; await api.delete(`/users/${id}`); fetchData(); };

  // Staff code CRUD
  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      await api.post('/staff-codes', { role: codeRole, label: codeLabel });
      setCodeDialogOpen(false); setCodeRole('manager'); setCodeLabel(''); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDeleteCode = async (id) => {
    if (!window.confirm('Delete this code?')) return;
    await api.delete(`/staff-codes/${id}`);
    fetchData();
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="users-page">
      <h1 className="page-title mb-6">Team Management</h1>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="codes" data-testid="tab-staff-codes">Staff Codes ({codes.length})</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="flex justify-end mb-4">
            <Dialog open={userDialogOpen} onOpenChange={(o) => { setUserDialogOpen(o); if (!o) { setEditing(null); setUserForm(defaultUserForm); } }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" />Add User</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit User' : 'Add User'}</DialogTitle></DialogHeader>
                <form onSubmit={handleUserSubmit} className="space-y-4">
                  {!editing && <div><Label>Email</Label><Input type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} required data-testid="user-email-input" /></div>}
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>First Name</Label><Input value={userForm.first_name} onChange={e => setUserForm({ ...userForm, first_name: e.target.value })} required data-testid="user-firstname-input" /></div>
                    <div><Label>Last Name</Label><Input value={userForm.last_name} onChange={e => setUserForm({ ...userForm, last_name: e.target.value })} required data-testid="user-lastname-input" /></div>
                  </div>
                  {!editing && <div><Label>Password</Label><Input type="password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} required data-testid="user-password-input" /></div>}
                  <div><Label>Phone</Label><Input value={userForm.phone} onChange={e => setUserForm({ ...userForm, phone: e.target.value })} data-testid="user-phone-input" /></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={userForm.role} onValueChange={v => setUserForm({ ...userForm, role: v })}>
                      <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="cleaner">Housekeeping</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="user-submit-btn">{editing ? 'Update' : 'Create'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="table-wrap">
            <table className="w-full data-table min-w-[550px]">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="font-medium text-slate-900">{u.first_name} {u.last_name}</td>
                    <td>{u.email}</td>
                    <td><Badge className={roleColors[u.role] || ''} variant="outline">{roleLabels[u.role] || u.role}</Badge></td>
                    <td><Badge className={u.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>{u.is_active !== false ? 'Active' : 'Inactive'}</Badge></td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Staff Codes Tab */}
        <TabsContent value="codes">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <KeyRound className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900">How Staff Codes Work</p>
                <p className="text-sm text-blue-700 mt-1">Generate a code and share it with your staff member. They visit the <strong>Staff Registration</strong> page, enter the code, and create their account. The code assigns their role automatically (Manager, Housekeeping, or Maintenance).</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end mb-4">
            <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="generate-code-btn"><KeyRound className="w-4 h-4 mr-2" />Generate Staff Code</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Generate Staff Code</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateCode} className="space-y-4">
                  <div>
                    <Label>Role</Label>
                    <Select value={codeRole} onValueChange={setCodeRole}>
                      <SelectTrigger data-testid="code-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="cleaner">Housekeeping</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Label (optional)</Label>
                    <Input value={codeLabel} onChange={e => setCodeLabel(e.target.value)} placeholder="e.g. For John Smith" data-testid="code-label-input" />
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="code-submit-btn">Generate Code</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-3">
            {codes.map(c => (
              <div key={c.id} className={`bg-white border rounded-lg p-4 flex items-center justify-between ${c.is_used ? 'border-slate-200 opacity-60' : 'border-slate-200'}`} data-testid={`code-row-${c.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`px-4 py-2 rounded-lg font-mono text-lg font-bold tracking-widest ${c.is_used ? 'bg-slate-100 text-slate-400' : 'bg-slate-900 text-white'}`} data-testid={`code-value-${c.id}`}>
                    {c.code}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={roleColors[c.role] || ''} variant="outline">{roleLabels[c.role] || c.role}</Badge>
                      {c.is_used ? (
                        <Badge className="bg-slate-100 text-slate-500">Used</Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                      )}
                    </div>
                    {c.label && <p className="text-xs text-slate-500 mt-1">{c.label}</p>}
                    {c.is_used && c.used_by_name && (
                      <p className="text-xs text-slate-500 mt-1">Used by: {c.used_by_name} ({c.used_by_email})</p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">
                      Created {c.created_at ? format(new Date(c.created_at), 'MMM dd, yyyy') : 'N/A'}
                      {c.expires_at && !c.is_used && ` \u00B7 Expires ${format(new Date(c.expires_at), 'MMM dd, yyyy')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!c.is_used && (
                    <Button variant="outline" size="sm" onClick={() => copyCode(c.code, c.id)} data-testid={`copy-code-${c.id}`}>
                      {copiedId === c.id ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteCode(c.id)} data-testid={`delete-code-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {codes.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <KeyRound className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No staff codes yet. Generate one to invite team members.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
